/**
 * InsightPro™ — Stock Optimisation Module v1.0
 *
 * Tabs:
 *   1. Overview       — Overstock / Understock / Zero stock KPIs + tables
 *   2. Transfers      — Inter-branch transfer recommendations
 *   3. Order Builder  — Local supplier order + Import/Overseas (voice + photo)
 *   4. Product Catalogue — New products photographed overseas
 *   5. Suppliers      — Manage supplier list
 *
 * Replaces Margin Analysis everywhere.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { pbiQuery } from '../utils/api';
import { useCompany } from '../context/CompanyContext';
import { getFirestore, collection, getDocs, addDoc, setDoc, doc,
         updateDoc, deleteDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

const db   = getFirestore();
const auth = getAuth();
const API_BASE = 'https://africa-south1-insightprofinal-3c793.cloudfunctions.net/dataApi';

// ── Formatters ────────────────────────────────────────────────
const fmt  = n => n == null ? '—' : `R ${Number(n).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
const fmtM = n => {
  if (n == null) return '—';
  if (n >= 1e6)  return `R ${(n/1e6).toFixed(2)}M`;
  if (n >= 1e3)  return `R ${(n/1e3).toFixed(0)}K`;
  return fmt(n);
};
const fmtN = n => n == null ? '—' : Number(n).toLocaleString('en-ZA', { maximumFractionDigits: 0 });

// ── Shared styles ─────────────────────────────────────────────
const card = {
  background: 'var(--card)', border: '1px solid var(--card-border)',
  borderRadius: 4, overflow: 'hidden',
};
const kpiCard = (accent) => ({
  background: 'var(--card)', border: '1px solid var(--card-border)',
  borderRadius: 4, padding: '16px 18px',
  borderTop: `2px solid ${accent}`,
});
const tabBtn = (active) => ({
  padding: '8px 16px',
  fontFamily: 'var(--font-body)', fontSize: 12.5, fontWeight: active ? 600 : 400,
  color: active ? 'var(--page-text)' : 'var(--page-muted)',
  borderBottom: active ? '2px solid var(--gold)' : '2px solid transparent',
  background: 'none', border: 'none', borderRadius: 0,
  cursor: 'pointer', transition: 'color 0.15s',
  whiteSpace: 'nowrap',
});
const btn = (variant = 'primary') => ({
  padding: '8px 18px', borderRadius: 3, cursor: 'pointer',
  fontFamily: 'var(--font-body)', fontSize: 12.5, fontWeight: 600,
  border: variant === 'primary' ? 'none' : '1px solid var(--card-border)',
  background: variant === 'primary' ? 'var(--gold)' : 'transparent',
  color: variant === 'primary' ? 'var(--void)' : 'var(--page-text)',
  transition: 'opacity 0.15s',
});
const input = {
  background: 'var(--card)', border: '1px solid var(--card-border)',
  borderRadius: 3, padding: '7px 10px', color: 'var(--page-text)',
  fontFamily: 'var(--font-body)', fontSize: 12.5, width: '100%',
  boxSizing: 'border-box',
};
const th = {
  fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: 'var(--page-muted)',
  padding: '9px 14px', textAlign: 'left', borderBottom: '1px solid var(--card-border)',
  background: 'var(--card)',
};
const td = {
  padding: '10px 14px', borderBottom: '1px solid var(--card-border)',
  fontSize: 12.5, color: 'var(--page-text, var(--page-text))',
};

// ── Urgency badge ─────────────────────────────────────────────
function UrgencyBadge({ days }) {
  const color = days <= 7 ? 'var(--red)' : days <= 14 ? 'var(--amber)' : 'var(--page-muted)';
  const label = days <= 7 ? 'CRITICAL' : days <= 14 ? 'WARNING' : 'MONITOR';
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 8.5,
      padding: '2px 7px', borderRadius: 2,
      background: `${color}22`, color, border: `1px solid ${color}55`,
    }}>{label}</span>
  );
}

// ── Loading spinner ───────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
      Loading…
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────
function Empty({ text }) {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
      {text}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 1 — OVERVIEW
// ═══════════════════════════════════════════════════════════════
function TabOverview({ companyId, period }) {
  const [stockData,      setStockData]      = useState(null);
  const [overstockData,  setOverstockData]  = useState([]);
  const [understockData, setUnderstockData] = useState([]);
  const [allRows,        setAllRows]        = useState([]);
  const [avgDaysOnHand,  setAvgDaysOnHand]  = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [filter,         setFilter]         = useState('all');
  const [drillCode,      setDrillCode]      = useState(null);
  const [drillBranch,    setDrillBranch]    = useState(null);   // { name, code }
  const [branchHistory,  setBranchHistory]  = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!companyId) { setLoading(true); return; }
    setLoading(true);
    Promise.all([
      pbiQuery({ table: 'StockSummary', companyId }),
      pbiQuery({ table: 'StockVelocity', companyId }),
    ]).then(([summary, velocity]) => {
      setStockData(summary?.rows?.[0] || null);
      const rows = velocity?.rows || [];

      // Compute avg days on hand from real days cover values
      const realDcRows = rows.filter(r => {
        const dc = r['[DaysCover]'] ?? r.DaysCover ?? 9999;
        return dc < 9999 && dc > 0;
      });
      const _avg = realDcRows.length > 0
        ? Math.round(realDcRows.reduce((s, r) => s + (r['[DaysCover]'] ?? r.DaysCover), 0) / realDcRows.length)
        : null;
      setAvgDaysOnHand(_avg);

      // Consolidate by stock code — sum OnHand/StockValue, keep worst DaysCover
      const byCode = {};
      rows.forEach(r => {
        const code = r['[StockCode]'] ?? r.StockCode ?? '';
        const dc   = r['[DaysCover]'] ?? r.DaysCover ?? 9999;
        const oh   = Number(r['[OnHand]'] ?? r.OnHand ?? 0);
        const sv   = Number(r['[StockValue]'] ?? r.StockValue ?? 0);
        if (!byCode[code]) {
          byCode[code] = { '[StockCode]': code, '[Description]': r['[Description]'] ?? r.Description ?? '',
            '[OnHand]': oh, '[StockValue]': sv, '[DaysCover]': dc, '[AvgCost]': r['[AvgCost]'] ?? r.AvgCost ?? 0 };
        } else {
          byCode[code]['[OnHand]']    += oh;
          byCode[code]['[StockValue]'] += sv;
          if (dc > byCode[code]['[DaysCover]']) byCode[code]['[DaysCover]'] = dc;
        }
      });
      const consolidated = Object.values(byCode);

      // Overstock = consolidated rows where worst DaysCover > 90
      const over = consolidated
        .filter(r => (r['[DaysCover]'] ?? 9999) > 90)
        .sort((a, b) => {
          const dcA = a['[DaysCover]'] ?? 9999;
          const dcB = b['[DaysCover]'] ?? 9999;
          if (dcA < 9999 && dcB >= 9999) return -1;
          if (dcA >= 9999 && dcB < 9999) return 1;
          if (dcA >= 9999 && dcB >= 9999) return (b['[StockValue]'] ?? 0) - (a['[StockValue]'] ?? 0);
          return dcB - dcA;
        });

      // Understock = consolidated rows with < 30 days cover
      const under = consolidated
        .filter(r => {
          const dc = r['[DaysCover]'] ?? 9999;
          return dc < 30 && (r['[OnHand]'] ?? 0) > 0;
        })
        .sort((a, b) => (a['[DaysCover]'] ?? 999) - (b['[DaysCover]'] ?? 999));

      setAllRows(rows);
      setOverstockData(over);
      setUnderstockData(under);
    }).catch(e => {
      console.error('StockOptimisation overview error:', e.message);
    }).finally(() => setLoading(false));
  }, [companyId, period]);

  const overstockValue = overstockData.reduce((s, r) => s + (r['[StockValue]'] ?? r.StockValue ?? 0), 0);
  const zeroStockLines = understockData.filter(r => (r.OnHand ?? r['[OnHand]']) === 0).length;

  if (loading) return <Spinner />;

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Total Stock Value',    value: fmtM(stockData?.['[TotalStockValue]'] ?? stockData?.TotalStockValue),          accent: 'var(--gold)' },
          { label: 'Avg Days on Hand',     value: avgDaysOnHand != null ? `${avgDaysOnHand} days` : '— days', accent: 'var(--page-muted)' },
          { label: 'Overstock Value',      value: fmtM(overstockValue),                       accent: 'var(--amber)' },
          { label: 'Understock Lines',     value: fmtN(understockData.length),                accent: 'var(--red)' },
          { label: 'Zero Stock Lines',     value: fmtN(stockData?.['[OutOfStockLines]'] ?? stockData?.OutOfStockLines ?? zeroStockLines), accent: 'var(--red)' },
        ].map(k => (
          <div key={k.label} style={kpiCard(k.accent)}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'all',        label: 'All' },
          { key: 'overstock',  label: `Overstock (${overstockData.length})` },
          { key: 'understock', label: `Understock (${understockData.length})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              ...btn(filter === f.key ? 'primary' : 'secondary'),
              padding: '5px 14px', fontSize: 12,
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Drill-down panel */}
      {drillCode && (() => {
        const code = drillCode;
        const drillRows = allRows.filter(r =>
          (r['[StockCode]'] ?? r.StockCode ?? '').toString().trim() === code.toString().trim()
        );
        const firstRow = drillRows[0] || {};
        const desc = firstRow['[Description]'] ?? firstRow.Description ?? code;
        const totalOH  = drillRows.reduce((s, r) => s + Number(r['[OnHand]']    ?? r.OnHand    ?? 0), 0);
        const totalVal = drillRows.reduce((s, r) => s + Number(r['[StockValue]'] ?? r.StockValue ?? 0), 0);
        const totalLay = drillRows.reduce((s, r) => s + Number(r['[Laybye]']    ?? r.Laybye    ?? 0), 0);
        return (
          <div style={{ background: 'var(--card)', border: '2px solid var(--gold)', borderRadius: 4, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                <button onClick={() => { setDrillCode(null); setDrillBranch(null); setBranchHistory([]); }}
                  style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0, flexShrink: 0 }}>←</button>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--gold)' }}>{code}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--page-text)' }}>{desc}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
                {[['Total On Hand', fmtN(totalOH)], ['On Laybye', fmtN(totalLay)], ['Total Value', fmtM(totalVal)]].map(([lbl, val]) => (
                  <div key={lbl} style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--page-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{lbl}</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
            {drillRows.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>No branch data found</div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                  <thead>
                    <tr>{['Branch', 'On Hand', 'On Laybye', 'Days Cover', 'Stock Value', 'Avg Cost'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {drillRows.map((r, i) => {
                      const dc = r['[DaysCover]'] ?? r.DaysCover;
                      return (
                        <tr key={i} 
                          style={{ background: drillBranch?.name === (r['[Branch]'] ?? r.Branch) ? 'rgba(200,149,32,0.06)' : 'transparent', cursor: 'pointer' }}
                          onClick={() => {
                            const branchName = r['[Branch]'] ?? r.Branch ?? '';
                            if (drillBranch?.name === branchName) { setDrillBranch(null); return; }
                            setDrillBranch({ name: branchName, code: drillCode });
                            setHistoryLoading(true);
                            setBranchHistory([]);
                            pbiQuery({ table: 'StockHistory', companyId, stockCode: drillCode, branchName })
                              .then(res => setBranchHistory(res?.rows || []))
                              .catch(() => setBranchHistory([]))
                              .finally(() => setHistoryLoading(false));
                          }}
                          onMouseEnter={e => { if (drillBranch?.name !== (r['[Branch]'] ?? r.Branch)) e.currentTarget.style.background = 'rgba(200,149,32,0.03)'; }}
                          onMouseLeave={e => { if (drillBranch?.name !== (r['[Branch]'] ?? r.Branch)) e.currentTarget.style.background = 'transparent'; }}>
                          <td style={{ ...td, fontWeight: 500, minWidth: 120, color: 'var(--gold)' }}>
                            {r['[Branch]'] ?? r.Branch ?? '—'} <span style={{ fontSize: 10, color: 'var(--page-muted)' }}>›</span>
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtN(r['[OnHand]'] ?? r.OnHand)}</td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtN(r['[Laybye]'] ?? r.Laybye ?? 0)}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap',
                            color: !dc || dc >= 9999 ? 'var(--page-muted)' : dc > 90 ? 'var(--amber)' : dc < 30 ? 'var(--red)' : 'var(--green)' }}>
                            {!dc || dc >= 9999 ? 'No sales' : `${Math.round(dc)}d`}
                          </td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtM(r['[StockValue]'] ?? r.StockValue)}</td>
                          <td style={{ ...td, textAlign: 'right', color: 'var(--page-muted)', whiteSpace: 'nowrap' }}>{fmtM(r['[AvgCost]'] ?? r.AvgCost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Branch History Panel */}
            {drillBranch && (
              <div style={{ borderTop: '2px solid var(--gold)', margin: '0' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>Branch History</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--page-text)' }}>{drillBranch.name}</div>
                  </div>
                  <button onClick={() => setDrillBranch(null)}
                    style={{ background: 'none', border: 'none', color: 'var(--page-muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                </div>
                {historyLoading ? (
                  <div style={{ padding: 24, textAlign: 'center' }}><Spinner /></div>
                ) : branchHistory.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>No sales history found for this branch</div>
                ) : (
                  <div style={{ padding: '12px 16px' }}>
                    {/* Mini bar chart */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.1em', marginBottom: 8 }}>MONTHLY REVENUE — LAST 24 MONTHS</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60, overflowX: 'auto' }}>
                        {(() => {
                          const maxRev = Math.max(...branchHistory.map(r => r['[Revenue]'] ?? r.Revenue ?? 0), 1);
                          return branchHistory.map((r, i) => {
                            const rev = r['[Revenue]'] ?? r.Revenue ?? 0;
                            const h = Math.round((rev / maxRev) * 56);
                            const label = `${(r['Date[MonthName]'] ?? r['[MonthName]'] ?? '').slice(0,3)} ${String(r['Date[Year]'] ?? r['[Year]'] ?? '').slice(2)}`;
                            return (
                              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}
                                title={`${label}: ${fmtM(rev)}`}>
                                <div style={{ width: 18, height: h || 2, background: h > 0 ? 'var(--gold)' : 'var(--card-border)', borderRadius: '2px 2px 0 0', transition: 'height 0.3s' }} />
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 7, color: 'var(--page-muted)', whiteSpace: 'nowrap', transform: 'rotate(-45deg)', marginTop: 4, transformOrigin: 'top left' }}>{label}</div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    {/* History table */}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 400 }}>
                        <thead>
                          <tr>{['Month', 'Revenue', 'Units Sold', 'GP (Rev-Pur)'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {[...branchHistory].reverse().map((r, i) => {
                            const rev = r['[Revenue]'] ?? r.Revenue ?? 0;
                            const units = r['[Units]'] ?? r['[UNITS_SOLD]'] ?? r.Units ?? 0;
                            const gp = (r['[Revenue]'] ?? r.Revenue ?? 0) - (r['[Purchases]'] ?? 0);
                            const label = `${r['Date[MonthName]'] ?? r['[MonthName]'] ?? ''} ${r['Date[Year]'] ?? r['[Year]'] ?? ''}`;
                            return (
                              <tr key={i} style={{ background: 'transparent' }}>
                                <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11, whiteSpace: 'nowrap' }}>{label}</td>
                                <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: rev > 0 ? 'var(--page-text)' : 'var(--page-muted)' }}>{rev > 0 ? fmtM(rev) : '—'}</td>
                                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{units > 0 ? fmtN(units) : '—'}</td>
                                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', color: gp > 0 ? 'var(--green)' : gp < 0 ? 'var(--red)' : 'var(--page-muted)' }}>
                                  {gp !== 0 ? fmtM(gp) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--card)', fontWeight: 700 }}>
                            <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 10 }}>TOTAL</td>
                            <td style={{ ...td, textAlign: 'right', color: 'var(--gold)' }}>{fmtM(branchHistory.reduce((s,r) => s + (r['[Revenue]'] ?? r.Revenue ?? 0), 0))}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{fmtN(branchHistory.reduce((s,r) => s + (r['[Units]'] ?? r['[UNITS_SOLD]'] ?? r.Units ?? 0), 0))}</td>
                            <td style={{ ...td, textAlign: 'right', color: 'var(--green)' }}>{fmtM(branchHistory.reduce((s,r) => s + ((r['[Revenue]'] ?? r.Revenue ?? 0) - (r['[Purchases]'] ?? 0)), 0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Overstock Table */}
      {(filter === 'all' || filter === 'overstock') && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--amber)', letterSpacing: '0.12em' }}>OVERSTOCK — DAYS COVER &gt; 90</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)' }}>{overstockData.length} lines</span>
          </div>
          {overstockData.length === 0 ? <Empty text="No overstock detected" /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Stock Code', 'Description', 'On Hand', 'Days Cover', 'Value'].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overstockData.slice(0, 200).map((r, i) => {
                    const dc   = r['[DaysCover]'] ?? r.DaysCover;
                    const code = r['[StockCode]'] ?? r.StockCode ?? '—';
                    return (
                      <tr key={i} style={{ background: 'transparent', cursor: 'pointer' }}
                        onClick={() => setDrillCode(code)}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,149,32,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--gold)', fontWeight: 600 }}>{code}</td>
                        <td style={{ ...td, minWidth: 0 }}>{r['[Description]'] ?? r.Description ?? '—'}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtN(r['[OnHand]'] ?? r.OnHand)}</td>
                        <td style={{ ...td, textAlign: 'right', color: !dc || dc >= 9999 ? 'var(--page-muted)' : 'var(--amber)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {!dc || dc >= 9999 ? 'No sales' : `${Math.round(dc)}d`}
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtM(r['[StockValue]'] ?? r.StockValue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Understock Table */}
      {(filter === 'all' || filter === 'understock') && (
        <div style={card}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--red)', letterSpacing: '0.12em' }}>UNDERSTOCK — DAYS COVER &lt; 30</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)' }}>{understockData.length} lines</span>
          </div>
          {understockData.length === 0 ? <Empty text="No understock detected" /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Stock Code', 'Description', 'On Hand', 'Days Cover', 'Urgency'].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {understockData.slice(0, 200).map((r, i) => {
                    const dc   = r['[DaysCover]'] ?? r.DaysCover;
                    const code = r['[StockCode]'] ?? r.StockCode ?? '—';
                    return (
                      <tr key={i} style={{ background: 'transparent', cursor: 'pointer' }}
                        onClick={() => setDrillCode(code)}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,149,32,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--gold)', fontWeight: 600 }}>{code}</td>
                        <td style={{ ...td, minWidth: 0 }}>{r['[Description]'] ?? r.Description ?? '—'}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtN(r['[OnHand]'] ?? r.OnHand)}</td>
                        <td style={{ ...td, textAlign: 'right', color: dc <= 7 ? 'var(--red)' : 'var(--amber)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {dc != null && dc < 9999 ? `${Math.round(dc)}d` : '—'}
                        </td>
                        <td style={td}><UrgencyBadge days={dc ?? 999} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 2 — INTER-BRANCH TRANSFERS
// ═══════════════════════════════════════════════════════════════
function TabTransfers({ companyId, period }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(true); return; }
    setLoading(true);
    pbiQuery({ table: 'StockVelocity', companyId })
      .then(res => {
        const data = res?.rows || [];

        // Group by stock code — find overstock and understock branches for same product
        const byCode = {};
        data.forEach(r => {
          const code = r['[StockCode]'] ?? r.StockCode ?? '';
          if (!byCode[code]) byCode[code] = { over: [], under: [] };
          const dc = r['[DaysCover]'] ?? r.DaysCover;
          if (dc > 90)       byCode[code].over.push(r);
          else if (dc < 30)  byCode[code].under.push(r);
        });

        const transfers = [];
        Object.entries(byCode).forEach(([code, { over, under }]) => {
          if (!over.length || !under.length) return;
          over.forEach(fromR => {
            under.forEach(toR => {
              const fromBranch = fromR['[Branch]'] ?? fromR.Branch ?? fromR['CompanyList[COMPANY NAME]'];
              const toBranch   = toR['[Branch]']  ?? toR.Branch   ?? toR['CompanyList[COMPANY NAME]'];
              if (fromBranch === toBranch) return;
              const fromDC = fromR['[DaysCover]'] ?? fromR.DaysCover;
              const toDC   = toR['[DaysCover]']   ?? toR.DaysCover;
              const fromOH = fromR['[OnHand]'] ?? fromR.OnHand ?? 0;
              const toWeekly = toR['[WeeklySales]'] ?? toR.WeeklySales ?? 1;
              // Transfer enough to give 'to' branch 60 days cover
              const transferQty = Math.max(0, Math.round(toWeekly * (60 / 7)));
              const actualTransfer = Math.min(transferQty, Math.floor(fromOH * 0.4)); // max 40% of from stock
              if (actualTransfer <= 0) return;
              transfers.push({
                code,
                description: fromR['[Description]'] ?? fromR.Description ?? '',
                fromBranch,
                toBranch,
                transferQty: actualTransfer,
                fromDC: Math.round(fromDC),
                toDCAfter: Math.round(toDC + (actualTransfer / (toWeekly / 7))),
                value: actualTransfer * (fromR['[AvgCost]'] ?? fromR.AvgCost ?? 0),
                approved: false,
              });
            });
          });
        });
        setRows(transfers.slice(0, 100));
      })
      .catch(e => console.error('StockVelocity error:', e.message))
      .finally(() => setLoading(false));
  }, [companyId, period]);

  const [approved, setApproved] = useState({});
  const toggle = i => setApproved(p => ({ ...p, [i]: !p[i] }));

  const exportExcel = () => {
    const approvedRows = rows.filter((_, i) => approved[i]);
    if (!approvedRows.length) { alert('Select at least one transfer to export.'); return; }
    const csv = ['Stock Code,Description,From Branch,To Branch,Qty,Approx Value',
      ...approvedRows.map(r => `${r.code},"${r.description}","${r.fromBranch}","${r.toBranch}",${r.transferQty},${r.value.toFixed(0)}`)
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `transfers_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Inter-Branch Transfer Recommendations</div>
          <div style={{ fontSize: 11.5, color: 'var(--page-muted)' }}>
            Products where one branch is overstocked (&gt;90d) and another is understocked (&lt;30d).
            Approve transfers then export.
          </div>
        </div>
        <button style={btn()} onClick={exportExcel}>Export Approved ↓</button>
      </div>

      <div style={card}>
        {rows.length === 0 ? <Empty text="No transfer opportunities detected" /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['✓', 'Code', 'Description', 'From Branch', 'To Branch', 'Qty', 'From Cover', 'To Cover After', 'Est. Value'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{
                    background: approved[i] ? 'rgba(200,149,32,0.08)' : 'transparent'
                  }}>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!approved[i]} onChange={() => toggle(i)}
                        style={{ cursor: 'pointer', accentColor: 'var(--gold)' }} />
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{r.code}</td>
                    <td style={{ ...td, maxWidth: 160, fontSize: 12 }}>{r.description}</td>
                    <td style={td}>{r.fromBranch}</td>
                    <td style={td}>{r.toBranch}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{r.transferQty}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--amber)' }}>{r.fromDC}d</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--green)' }}>{r.toDCAfter}d</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtM(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 3 — ORDER BUILDER
// ═══════════════════════════════════════════════════════════════

// Fetch suppliers live from Power BI Creditors table (always up to date from ERP)
// Falls back to Firestore suppliers if Creditors table doesn't exist
function useSuppliers(companyId) {
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    // Try Power BI Creditors first
    pbiQuery({
      companyId,
      dax: `EVALUATE SELECTCOLUMNS(FILTER('Creditors', 'Creditors'[TOTAL] > 0), "Name", 'Creditors'[NAME], "Account", 'Creditors'[ACCOUNT], "Email", 'Creditors'[EMAIL], "Phone", 'Creditors'[TELEPHONE1])`,
    })
      .then(r => {
        const rows = r?.rows || [];
        if (rows.length > 0) {
          setSuppliers(rows.map((row, i) => ({
            id:    row['[Account]'] || row['Account'] || String(i),
            name:  row['[Name]']    || row['Name']    || '',
            email: row['[Email]']   || row['Email']   || '',
            phone: row['[Phone]']   || row['Phone']   || '',
          })).filter(s => s.name));
        } else {
          // Fallback to Firestore
          getDocs(collection(db, 'companies', companyId, 'suppliers'))
            .then(snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
            .catch(() => {});
        }
      })
      .catch(() => {
        // Creditors table doesn't exist — fall back to Firestore
        getDocs(collection(db, 'companies', companyId, 'suppliers'))
          .then(snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
          .catch(() => {});
      });
  }, [companyId]);
  return suppliers;
}

function useDepartments(companyId) {
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    // Fetch distinct departments from Stock table (always available)
    pbiQuery({
      companyId,
      dax: `EVALUATE DISTINCT(SELECTCOLUMNS('Stock', "Code", 'Stock'[DEPARTMENT]))`,
    })
      .then(r => {
        const rows = r?.rows || [];
        if (rows.length === 0) return;
        console.log('[useDepartments] raw rows:', JSON.stringify(rows.slice(0,3)));
        const deptCodes = rows.map(row => row['[Code]'] || row['Code'] || '').filter(Boolean);
        console.log('[useDepartments] codes:', deptCodes.slice(0,10));
        // Try to get department names from Departments table
        pbiQuery({
          companyId,
          dax: `EVALUATE SELECTCOLUMNS('Departments', "Code", 'Departments'[Dept], "Name", 'Departments'[DeptName])`,
        })
          .then(r2 => {
            const nameRows = r2?.rows || [];
            const nameMap = {};
            nameRows.forEach(row => {
              const code = row['[Code]'] || row['Code'] || '';
              const name = row['[Name]'] || row['Name'] || '';
              if (code) nameMap[code] = name;
            });
            setDepartments(deptCodes.map(code => ({
              code,
              name: nameMap[code] || code,
            })).sort((a, b) => (a.name || a.code).localeCompare(b.name || b.code)));
          })
          .catch(() => {
            // No Departments table — just use codes
            setDepartments(deptCodes.map(code => ({ code, name: code })).sort((a, b) => a.code.localeCompare(b.code)));
          });
      })
      .catch(e => console.warn('Could not load departments:', e));
  }, [companyId]);
  return departments;
}

// Period labels for forecast basis
const FORECAST_OPTIONS = [
  { key: 'last30',    label: 'Last 30 Days' },
  { key: 'last3m',   label: 'Last 3 Months' },
  { key: 'last6m',   label: 'Last 6 Months' },
  { key: 'sameLY',   label: 'Same Period Last Year' },
  { key: 'bestOf',   label: 'Best of: Last 3m vs Same LY' },
  { key: 'custom',   label: 'Custom Range' },
];

function TabOrderBuilder({ companyId }) {
  const [mode,         setMode]         = useState('local'); // local | import
  const [supplier,     setSupplier]     = useState('');
  const [department,   setDepartment]   = useState('');
  const [coverWeeks,   setCoverWeeks]   = useState(4);
  const [forecastBasis,setForecastBasis]= useState('last3m');
  const [safetyBuffer, setSafetyBuffer] = useState(10);
  const [orderLines,   setOrderLines]   = useState([]);
  const [building,     setBuilding]     = useState(false);
  const [clerkEmail,   setClerkEmail]   = useState('');
  const [sending,      setSending]      = useState(false);
  const [sendStatus,   setSendStatus]   = useState('');
  const [voice,        setVoice]        = useState('');
  const [listening,    setListening]    = useState(false);
  const [photoMatch,   setPhotoMatch]   = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [sortCol,      setSortCol]     = useState(null);
  const [sortAsc,      setSortAsc]     = useState(true);
  const fileRef = useRef();
  const suppliers = useSuppliers(companyId);
  const departments = useDepartments(companyId);
  const recogRef = useRef(null);

  // Fetch clerk email from company procurement settings
  useEffect(() => {
    if (!companyId) return;
    getDocs(collection(db, 'companies', companyId, 'settings'))
      .then(snap => {
        const s = snap.docs.find(d => d.id === 'procurement');
        if (s) setClerkEmail(s.data().clerkEmail || '');
      }).catch(() => {});
  }, [companyId]);

  // ── Build order from stock data + cover weeks ──────────────
  const buildOrder = async () => {
    console.log('[OrderBuilder] buildOrder called, companyId:', companyId);
    if (!companyId) { alert('No company selected. Please go to Dashboard first.'); return; }
    setBuilding(true);
    try {
      // Build DAX filtered by supplier and forecast basis
      const now = new Date();
      const y = now.getFullYear(), m = now.getMonth() + 1, d = now.getDate();

      // Compute YYYYMM cutoffs and week divisors based on forecastBasis
      const yyyymm = (yr, mo) => { while (mo <= 0) { yr--; mo += 12; } return yr * 100 + mo; };
      let dateFilter, weeksDivisor;

      if (forecastBasis === 'last30') {
        // Last 30 days — use DATE filter, divide by ~4.3 weeks
        dateFilter = `FILTER(ALL('Date'), 'Date'[Date] >= TODAY() - 30)`;
        weeksDivisor = 30 / 7;
      } else if (forecastBasis === 'last6m') {
        const cutoff = yyyymm(y, m - 6);
        dateFilter = `FILTER(ALL('Date'), 'Date'[YYYYMM] >= ${cutoff})`;
        weeksDivisor = 26;
      } else if (forecastBasis === 'sameLY') {
        // Same 3-month window last year
        const lyStart = yyyymm(y - 1, m - 2);
        const lyEnd   = yyyymm(y - 1, m);
        dateFilter = `FILTER(ALL('Date'), 'Date'[YYYYMM] >= ${lyStart} && 'Date'[YYYYMM] <= ${lyEnd})`;
        weeksDivisor = 13;
      } else {
        // default: last3m (also used for bestOf and custom as base)
        const cutoff = yyyymm(y, m - 3);
        dateFilter = `FILTER(ALL('Date'), 'Date'[YYYYMM] >= ${cutoff})`;
        weeksDivisor = 13;
      }

      // Build stock table filter — supplier and/or department
      let stockFilter = `'Stock'`;
      const filters = [];
      if (supplier) filters.push(`'Stock'[MainSupplier] = "${supplier.replace(/"/g, '')}"`);
      if (department) filters.push(`'Stock'[DEPARTMENT] = "${department.replace(/"/g, '')}"`);
      if (filters.length > 0) {
        stockFilter = `CALCULATETABLE('Stock', ${filters.join(', ')})`;
      }

      // Include department code from Stock table (always available)
      const deptCol = `"_Dept", 'Stock'[DEPARTMENT]`;

      // For 'bestOf' we need both current and LY queries
      let dax;
      if (forecastBasis === 'bestOf') {
        const cutoff3m  = yyyymm(y, m - 3);
        const lyStart   = yyyymm(y - 1, m - 2);
        const lyEnd     = yyyymm(y - 1, m);
        dax = `
          EVALUATE
          ADDCOLUMNS(
            ${stockFilter},
            "_OnHand",      CALCULATE(SUM('StockBalances'[ONHAND])),
            "_AvgCost",     CALCULATE(AVERAGE('Stock'[AVRGCOST])),
            "_StockValue",  CALCULATE(SUMX('StockBalances', 'StockBalances'[ONHAND] * RELATED('Stock'[AVRGCOST]))),
            "_Units3M",     CALCULATE(SUM('Transactions'[UNITS_SOLD]), FILTER(ALL('Date'), 'Date'[YYYYMM] >= ${cutoff3m})),
            "_UnitsLY",     CALCULATE(SUM('Transactions'[UNITS_SOLD]), FILTER(ALL('Date'), 'Date'[YYYYMM] >= ${lyStart} && 'Date'[YYYYMM] <= ${lyEnd})),
            "_WeeklyRate",  DIVIDE(MAX(
              CALCULATE(SUM('Transactions'[UNITS_SOLD]), FILTER(ALL('Date'), 'Date'[YYYYMM] >= ${cutoff3m})),
              CALCULATE(SUM('Transactions'[UNITS_SOLD]), FILTER(ALL('Date'), 'Date'[YYYYMM] >= ${lyStart} && 'Date'[YYYYMM] <= ${lyEnd}))
            ), 13),
            ${deptCol}
          )`;
      } else {
        dax = `
          EVALUATE
          ADDCOLUMNS(
            ${stockFilter},
            "_OnHand",      CALCULATE(SUM('StockBalances'[ONHAND])),
            "_AvgCost",     CALCULATE(AVERAGE('Stock'[AVRGCOST])),
            "_StockValue",  CALCULATE(SUMX('StockBalances', 'StockBalances'[ONHAND] * RELATED('Stock'[AVRGCOST]))),
            "_UnitsForecast", CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${dateFilter}),
            "_WeeklyRate",  DIVIDE(CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${dateFilter}), ${weeksDivisor}),
            ${deptCol}
          )`;
      }

      console.log('[OrderBuilder] department:', JSON.stringify(department), 'supplier:', JSON.stringify(supplier), 'departments list:', departments.length);
      console.log('[OrderBuilder] DAX:', dax);
      const res = await pbiQuery({ dax, companyId });
      console.log('[OrderBuilder] response:', res);
      const rows = res?.rows || [];

      if (rows.length === 0) {
        const filterDesc = [supplier && `supplier "${supplier}"`, department && `department "${department}"`].filter(Boolean).join(' and ') || 'the selected filters';
        alert(`No stock items found for ${filterDesc}.`);
        setBuilding(false);
        return;
      }

      const lines = rows.map(r => {
        // Power BI returns columns with table prefix e.g. "Stock[CODE]"
        const weekly   = r['[_WeeklyRate]']       ?? r['_WeeklyRate']       ?? 0;
        const onHand   = r['[_OnHand]']           ?? r['_OnHand']           ?? 0;
        const avgCost  = r['[_AvgCost]']          ?? r['_AvgCost']          ?? 0;
        const code     = r['Stock[CODE]']         ?? r['Stock[StockCode]']   ?? r['[CODE]']            ?? r['CODE']            ?? '';
        const desc     = r['Stock[DESCRIPTION]']  ?? r['Stock[Description]'] ?? r['[DESCRIPTION]']     ?? r['DESCRIPTION']     ?? '';
        const supp     = r['Stock[MainSupplier]'] ?? r['[MainSupplier]']    ?? r['MainSupplier']    ?? '';
        const deptCode = r['[_Dept]']              ?? r['_Dept']             ?? r['Stock[DEPARTMENT]'] ?? r['[DEPARTMENT]'] ?? '';
        const dept     = departments.find(d => d.code === deptCode)?.name || deptCode;
        const coverage = coverWeeks * 7;
        const required = Math.ceil((weekly / 7) * coverage * (1 + safetyBuffer / 100));
        const orderQty = Math.max(0, required - onHand);
        return {
          code,
          description:  desc,
          supplier:     supp,
          department:   dept,
          onHand,
          weeksCover:   weekly > 0 ? Math.round(onHand / (weekly / 7) / 7) : 9999,
          weeklySales:  weekly,
          suggestedQty: orderQty,
          qty:          orderQty,
          unitCost:     avgCost,
          isNew:        false,
        };
      }).filter(l => l.code);

      setOrderLines(lines);
      if (lines.length === 0) {
        alert('No stock items found for the selected filters.');
      }
    } catch (e) {
      alert('Could not load stock data: ' + e.message);
    } finally {
      setBuilding(false);
    }
  };

  // ── Voice input ────────────────────────────────────────────
  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Speech recognition not supported in this browser. Use Chrome on desktop or Android.'); return; }
    const r = new SR();
    r.lang = 'en-ZA'; r.continuous = false; r.interimResults = false;
    r.onresult  = e => setVoice(e.results[0][0].transcript);
    r.onend     = () => setListening(false);
    r.onerror   = () => setListening(false);
    recogRef.current = r; r.start(); setListening(true);
  };

  const processVoice = async () => {
    if (!voice || !companyId) return;
    setBuilding(true);
    try {
      // Extract requirements from voice transcript via server-side Claude proxy
      const idToken = await auth.currentUser?.getIdToken();
      const aiRes = await fetch(`${API_BASE}/stockAI`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'voice', transcript: voice }),
      });
      if (!aiRes.ok) {
        const err = await aiRes.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${aiRes.status}`);
      }
      const parsed = (await aiRes.json()).result;

      // Now build the order based on extracted categories
      const stockRes = await pbiQuery({ table: 'StockVelocity', companyId });
      const stockRows = stockRes?.rows || [];

      const matchedLines = stockRows.filter(r => {
        if (!parsed.categories?.length) return true;
        const desc = (r['[Description]'] ?? r.Description ?? '').toLowerCase();
        return parsed.categories.some(cat => desc.includes(cat.toLowerCase()));
      }).map(r => {
        const weekly = r['[WeeklySales]'] ?? r.WeeklySales ?? 0;
        const onHand = r['[OnHand]'] ?? r.OnHand ?? 0;
        const target = weekly * coverWeeks;
        const orderQty = Math.max(0, Math.ceil(target) - onHand);
        return {
          code:        r['[StockCode]'] ?? r.StockCode ?? '',
          description: r['[Description]'] ?? r.Description ?? '',
          onHand, daysCover: Math.round(r['[DaysCover]'] ?? r.DaysCover ?? 0),
          weeklySales: weekly, suggestedQty: orderQty, qty: orderQty,
          unitCost: r['[AvgCost]'] ?? r.AvgCost ?? 0,
          isNew: false,
        };
      }).filter(l => l.qty > 0);

      setOrderLines(matchedLines);
      if (parsed.supplier) setSupplier(parsed.supplier);
    } catch (e) {
      alert('Could not process voice input: ' + e.message);
    } finally {
      setBuilding(false);
    }
  };

  // ── Photo recognition ──────────────────────────────────────
  const handlePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPhotoLoading(true); setPhotoMatch(null);
    try {
      // Read as base64
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });

      // Get product catalogue for matching
      const stockRes = await pbiQuery({ table: 'StockVelocity', companyId });
      const stockRows = (stockRes?.rows || []).slice(0, 200); // limit for token budget
      const catalogue = stockRows.map(r =>
        `${r['[StockCode]'] ?? r.StockCode}: ${r['[Description]'] ?? r.Description}`
      ).join('\n');

      // Send to server-side Claude proxy
      const idToken = await auth.currentUser?.getIdToken();
      const aiRes = await fetch(`${API_BASE}/stockAI`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'photo', imageBase64: base64, mediaType: file.type, catalogue }),
      });
      if (!aiRes.ok) {
        const err = await aiRes.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${aiRes.status}`);
      }
      const parsed = (await aiRes.json()).result;
      setPhotoMatch({ ...parsed, imageFile: file });
    } catch (e) {
      alert('Photo recognition failed: ' + e.message);
    } finally {
      setPhotoLoading(false);
    }
  };

  const addPhotoMatch = () => {
    if (!photoMatch) return;
    if (photoMatch.matched) {
      // Find existing line or add new one
      const existing = orderLines.find(l => l.code === photoMatch.stockCode);
      if (!existing) {
        setOrderLines(prev => [...prev, {
          code: photoMatch.stockCode, description: photoMatch.description,
          onHand: 0, daysCover: 0, weeklySales: 0,
          suggestedQty: 1, qty: 1, unitCost: 0, isNew: false,
        }]);
      }
    } else {
      setOrderLines(prev => [...prev, {
        code: 'NEW', description: photoMatch.suggestedDescription || 'New Product',
        onHand: 0, daysCover: 0, weeklySales: 0,
        suggestedQty: 1, qty: 1, unitCost: 0, isNew: true,
      }]);
    }
    setPhotoMatch(null);
  };

  // ── Update qty in order table ──────────────────────────────
  const updateQty = (i, val) => {
    setOrderLines(prev => prev.map((l, idx) => idx === i ? { ...l, qty: Number(val) || 0 } : l));
  };

  const removeLine = i => setOrderLines(prev => prev.filter((_, idx) => idx !== i));

  const totalValue = orderLines.reduce((s, l) => s + l.qty * l.unitCost, 0);

  // Sortable column config
  const SORT_COLS = [
    { key: 'code',        label: 'Code',       type: 'string' },
    { key: 'description', label: 'Description', type: 'string' },
    { key: 'department',  label: 'Department',  type: 'string' },
    { key: 'onHand',      label: 'On Hand',     type: 'number' },
    { key: 'weeksCover',  label: 'Wks Cover',   type: 'number' },
    { key: 'weeklySales', label: 'Wkly Sales',  type: 'number' },
    { key: 'suggestedQty',label: 'Suggested',   type: 'number' },
    { key: 'qty',         label: 'Your Qty',    type: 'number' },
    { key: 'unitCost',    label: 'Unit Cost',   type: 'number' },
    { key: 'lineTotal',   label: 'Line Total',  type: 'number' },
    { key: '',            label: '',             type: 'none' },
  ];

  const handleSort = (key) => {
    if (!key) return;
    if (sortCol === key) { setSortAsc(!sortAsc); }
    else { setSortCol(key); setSortAsc(true); }
  };

  const sortedLines = React.useMemo(() => {
    if (!sortCol) return orderLines;
    const col = SORT_COLS.find(c => c.key === sortCol);
    if (!col) return orderLines;
    const dir = sortAsc ? 1 : -1;
    return [...orderLines].sort((a, b) => {
      let va = sortCol === 'lineTotal' ? a.qty * a.unitCost : a[sortCol];
      let vb = sortCol === 'lineTotal' ? b.qty * b.unitCost : b[sortCol];
      if (col.type === 'string') return dir * String(va || '').localeCompare(String(vb || ''));
      return dir * ((Number(va) || 0) - (Number(vb) || 0));
    });
  }, [orderLines, sortCol, sortAsc]);

  // ── Export Excel (IQ-ready format) ────────────────────────
  const exportExcel = () => {
    const existing = orderLines.filter(l => !l.isNew);
    const newLines  = orderLines.filter(l => l.isNew);

    let csv = 'StockCode,Department,OrderQty\n';
    csv += existing.map(l => `${l.code},"${l.department || ''}",${l.qty}`).join('\n');
    if (newLines.length) {
      csv += '\n\nNEW PRODUCTS (create stock codes first)\n';
      csv += 'Description,Qty,Est Unit Cost\n';
      csv += newLines.map(l => `"${l.description}",${l.qty},${l.unitCost}`).join('\n');
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const filterName = [supplier, department].filter(Boolean).join('_') || 'order';
    a.download = `order_${filterName}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // ── Email order ────────────────────────────────────────────
  const emailOrder = async () => {
    if (!clerkEmail) { alert('No clerk email configured. Set it in Admin → Client Settings → Procurement.'); return; }
    if (!orderLines.length) { alert('No lines in order.'); return; }
    setSending(true); setSendStatus('');
    try {
      const idToken = await getAuth().currentUser?.getIdToken();
      const res = await fetch('https://africa-south1-insightprofinal-3c793.cloudfunctions.net/sendOrderEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          companyId,
          clerkEmail,
          supplier: supplier || 'Unspecified Supplier',
          coverWeeks,
          totalLines: orderLines.length,
          totalValue,
          orderLines: orderLines.map(l => ({
            code: l.code, description: l.description,
            qty: l.qty, unitCost: l.unitCost, isNew: l.isNew,
          })),
        }),
      });
      const data = await res.json();
      if (data.success) setSendStatus('✓ Order emailed to ' + clerkEmail);
      else setSendStatus('Error: ' + (data.error || 'Unknown error'));
    } catch (e) {
      setSendStatus('Error: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* Mode switcher */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, ...card, overflow: 'hidden', width: 'fit-content' }}>
        {[{ key: 'local', label: '🏭 Local Supplier Order' }, { key: 'import', label: '✈️ Import / Overseas Order' }].map(m => (
          <button key={m.key} onClick={() => setMode(m.key)}
            style={{
              padding: '10px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: 'none', fontFamily: 'var(--font-body)',
              background: mode === m.key ? 'var(--gold)' : 'transparent',
              color: mode === m.key ? 'var(--void)' : 'var(--page-text)',
            }}>
            {m.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 24 }}>

        {/* Configuration panel */}
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
            {mode === 'local' ? 'Order Configuration' : 'Import Configuration'}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11.5, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>Supplier</label>
              <input list="suppliers-list" value={supplier} onChange={e => setSupplier(e.target.value)}
                placeholder="e.g. KIC, Samsung, LG…" style={input} />
              <datalist id="suppliers-list">
                {suppliers.map(s => <option key={s.id} value={s.name} />)}
              </datalist>
            </div>

            <div>
              <label style={{ fontSize: 11.5, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>
                Department {departments.length > 0 ? `(${departments.length})` : '(loading…)'}
              </label>
              <select value={department} onChange={e => { console.log('[OrderBuilder] dept selected:', e.target.value); setDepartment(e.target.value); }}
                style={{ ...input, cursor: 'pointer' }}>
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d.code} value={d.code}>{d.name || d.code}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 11.5, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>
                Cover for (weeks)
              </label>
              <input type="number" min={1} max={52} value={coverWeeks}
                onChange={e => setCoverWeeks(Number(e.target.value))} style={input} />
            </div>

            <div>
              <label style={{ fontSize: 11.5, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>
                Forecast Basis
              </label>
              <select value={forecastBasis} onChange={e => setForecastBasis(e.target.value)}
                style={{ ...input, cursor: 'pointer' }}>
                {FORECAST_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 11.5, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>
                Safety Stock Buffer %
              </label>
              <input type="number" min={0} max={50} value={safetyBuffer}
                onChange={e => setSafetyBuffer(Number(e.target.value))} style={input} />
            </div>

            <button style={{ ...btn(), marginTop: 4 }} onClick={buildOrder} disabled={building}>
              {building ? 'Building…' : '▶ Build Order'}
            </button>
          </div>
        </div>

        {/* Voice input — import mode only */}
        {mode === 'import' && (
          <div style={{ ...card, padding: 20 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
              Voice Input
            </div>
            <button
              onClick={startListening} disabled={listening}
              style={{
                ...btn(listening ? 'primary' : 'secondary'),
                width: '100%', marginBottom: 12, padding: '14px',
                background: listening ? 'rgba(212,95,95,0.15)' : 'var(--void-3)',
                border: `1px solid ${listening ? 'var(--red)' : 'var(--card-border)'}`,
                color: listening ? 'var(--red)' : 'var(--page-muted)',
                fontSize: 28,
              }}>
              {listening ? '🔴' : '🎤'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--page-muted)', marginBottom: 8 }}>
              {listening ? 'Listening… speak your requirements' : 'Click to speak your requirements'}
            </div>
            {voice && (
              <>
                <div style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 3, padding: '8px 10px', fontSize: 12, marginBottom: 10, fontStyle: 'italic', color: 'var(--page-muted)' }}>
                  "{voice}"
                </div>
                <button style={{ ...btn(), width: '100%' }} onClick={processVoice} disabled={building}>
                  {building ? 'Processing…' : 'Process & Build Order'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Photo recognition — import mode only */}
        {mode === 'import' && (
          <div style={{ ...card, padding: 20 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
              Photo Recognition
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={photoLoading}
              style={{
                ...btn('secondary'), width: '100%', padding: 14, marginBottom: 12,
                fontSize: 28, background: 'var(--card)',
              }}>
              📷
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }} onChange={handlePhoto} />
            <div style={{ fontSize: 11, color: 'var(--page-muted)', marginBottom: 12 }}>
              {photoLoading ? 'Analysing photo…' : 'Take a photo of a product to identify it or add as new'}
            </div>

            {photoMatch && (
              <div style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 3, padding: 12 }}>
                {photoMatch.matched ? (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)', marginBottom: 4 }}>
                      ✓ Product Identified
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)' }}>{photoMatch.stockCode}</div>
                    <div style={{ fontSize: 12, marginBottom: 8 }}>{photoMatch.description}</div>
                    <div style={{ fontSize: 10, color: 'var(--page-muted)', marginBottom: 10 }}>
                      Confidence: {photoMatch.confidence}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--amber)', marginBottom: 4 }}>
                      New Product
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 8 }}>{photoMatch.suggestedDescription}</div>
                    <div style={{ fontSize: 10, color: 'var(--page-muted)', marginBottom: 10 }}>
                      Category: {photoMatch.category}
                    </div>
                  </>
                )}
                <button style={{ ...btn(), width: '100%', fontSize: 12 }} onClick={addPhotoMatch}>
                  Add to Order
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Table */}
      {orderLines.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--gold)', letterSpacing: '0.12em' }}>
                ORDER — {[supplier, department && departments.find(d => d.code === department)?.name].filter(Boolean).join(' / ') || 'ALL ITEMS'}
              </span>
              <span style={{ marginLeft: 12, fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)' }}>
                {orderLines.length} lines · {fmtM(totalValue)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...btn('secondary'), fontSize: 12 }} onClick={exportExcel}>↓ Download Excel (IQ)</button>
              <button style={{ ...btn(), fontSize: 12 }} onClick={emailOrder} disabled={sending}>
                {sending ? 'Sending…' : '✉ Email to Clerk'}
              </button>
            </div>
          </div>
          {sendStatus && (
            <div style={{ padding: '8px 16px', fontSize: 11.5, color: sendStatus.startsWith('✓') ? 'var(--green)' : 'var(--red)', borderBottom: '1px solid var(--card-border)' }}>
              {sendStatus}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {SORT_COLS.map(col => (
                    <th key={col.label} style={{ ...th, cursor: col.key ? 'pointer' : 'default', userSelect: 'none' }}
                      onClick={() => handleSort(col.key)}>
                      {col.label}
                      {sortCol === col.key ? (sortAsc ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedLines.map((l) => {
                  const origIdx = orderLines.indexOf(l);
                  return (
                  <tr key={l.code || origIdx} style={{
                    background: l.isNew ? 'rgba(200,149,32,0.06)' : 'transparent'
                  }}>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {l.code} {l.isNew && <span style={{ fontSize: 8, color: 'var(--amber)', border: '1px solid var(--amber)', padding: '1px 4px', borderRadius: 2, marginLeft: 4 }}>NEW</span>}
                    </td>
                    <td style={{ ...td, maxWidth: 180, fontSize: 12 }}>{l.description}</td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--page-muted)' }}>{l.department}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtN(l.onHand)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{l.weeksCover}w</td>
                    <td style={{ ...td, textAlign: 'right' }}>{l.weeklySales?.toFixed(1)}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--page-muted)' }}>{l.suggestedQty}</td>
                    <td style={{ ...td, padding: '6px 12px', width: 70 }}>
                      <input type="number" min={0} value={l.qty}
                        onChange={e => updateQty(origIdx, e.target.value)}
                        style={{ ...input, padding: '4px 6px', textAlign: 'right', width: 70 }} />
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmt(l.unitCost)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(l.qty * l.unitCost)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button onClick={() => removeLine(origIdx)}
                        style={{ background: 'none', border: 'none', color: 'var(--page-muted)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>
                        ×
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--card)' }}>
                  <td colSpan={9} style={{ ...td, textAlign: 'right', fontWeight: 700, fontFamily: 'var(--font-mono)', fontSize: 11 }}>TOTAL</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--gold)' }}>{fmtM(totalValue)}</td>
                  <td style={td} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {orderLines.length === 0 && (
        <div style={{ ...card, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>{mode === 'import' ? '✈️' : '🏭'}</div>
          <div style={{ fontSize: 13, color: 'var(--page-muted)', marginBottom: 6 }}>
            {mode === 'local' ? 'Configure and click "Build Order" to generate a suggested reorder list' : 'Use voice input, photo recognition, or build an order manually'}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 4 — PRODUCT CATALOGUE
// ═══════════════════════════════════════════════════════════════
function TabProductCatalogue({ companyId }) {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    getDocs(collection(db, 'companies', companyId, 'productCatalogue'))
      .then(snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const filtered = products.filter(p =>
    !search || p.description?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase()) ||
    p.supplierName?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Product Catalogue</div>
          <div style={{ fontSize: 11.5, color: 'var(--page-muted)' }}>
            New products photographed during import trips. Link to IQ stock codes once created.
          </div>
        </div>
        <input placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...input, width: 200 }} />
      </div>

      <div style={card}>
        {filtered.length === 0 ? (
          <Empty text={products.length === 0 ? 'No products in catalogue yet — add via the Order Builder photo tool' : 'No results for "' + search + '"'} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Image', 'Description', 'Category', 'Supplier', 'Country', 'Est. Cost', 'Stock Code', 'First Ordered', 'Total Ordered'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p.id} style={{ background: 'transparent' }}>
                    <td style={{ ...td, padding: '6px 12px' }}>
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.description}
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 3, border: '1px solid var(--card-border)' }} />
                      ) : (
                        <div style={{ width: 40, height: 40, background: 'var(--card)', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📦</div>
                      )}
                    </td>
                    <td style={{ ...td, fontWeight: 500 }}>{p.description}</td>
                    <td style={{ ...td, color: 'var(--page-muted)' }}>{p.category}</td>
                    <td style={td}>{p.supplierName}</td>
                    <td style={td}>{p.supplierCountry}</td>
                    <td style={td}>{fmt(p.estimatedCost)}</td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {p.stockCode || <span style={{ color: 'var(--amber)', fontSize: 10 }}>Not yet linked</span>}
                    </td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--page-muted)' }}>
                      {p.firstOrderDate ? new Date(p.firstOrderDate).toLocaleDateString('en-ZA') : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{p.totalOrdered ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 5 — SUPPLIERS
// ═══════════════════════════════════════════════════════════════
function TabSuppliers({ companyId }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [form,      setForm]      = useState(null); // null = no form, {} = new, { id, ...} = edit
  const [saving,    setSaving]    = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    getDocs(collection(db, 'companies', companyId, 'suppliers'))
      .then(snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => setForm({ name: '', contactName: '', email: '', phone: '', currency: 'ZAR', leadTimeDays: 14 });
  const openEdit = (s) => setForm({ ...s });
  const closeForm = () => setForm(null);

  const save = async () => {
    if (!form.name) { alert('Supplier name is required'); return; }
    setSaving(true);
    try {
      const { id, ...data } = form;
      data.updatedAt = serverTimestamp();
      if (id) {
        await updateDoc(doc(db, 'companies', companyId, 'suppliers', id), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'companies', companyId, 'suppliers'), data);
      }
      closeForm(); load();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    await deleteDoc(doc(db, 'companies', companyId, 'suppliers', id));
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Suppliers</div>
          <div style={{ fontSize: 11.5, color: 'var(--page-muted)' }}>Manage your supplier list for the Order Builder.</div>
        </div>
        <button style={btn()} onClick={openNew}>+ Add Supplier</button>
      </div>

      {form && (
        <div style={{ ...card, padding: 20, marginBottom: 20, borderColor: 'var(--border-gold)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--gold)', letterSpacing: '0.12em', marginBottom: 14 }}>
            {form.id ? 'EDIT SUPPLIER' : 'NEW SUPPLIER'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
            {[
              { key: 'name',         label: 'Supplier Name *' },
              { key: 'contactName',  label: 'Contact Name' },
              { key: 'email',        label: 'Email' },
              { key: 'phone',        label: 'Phone' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ fontSize: 11, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>{f.label}</label>
                <input value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  style={input} />
              </div>
            ))}
            <div>
              <label style={{ fontSize: 11, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>Currency</label>
              <select value={form.currency || 'ZAR'} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}
                style={{ ...input, cursor: 'pointer' }}>
                {['ZAR', 'USD', 'CNY', 'EUR', 'GBP'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--page-muted)', display: 'block', marginBottom: 4 }}>Lead Time (days)</label>
              <input type="number" min={1} value={form.leadTimeDays || 14}
                onChange={e => setForm(p => ({ ...p, leadTimeDays: Number(e.target.value) }))}
                style={input} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn()} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Supplier'}</button>
            <button style={btn('secondary')} onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}

      <div style={card}>
        {suppliers.length === 0 ? <Empty text="No suppliers added yet" /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Name', 'Contact', 'Email', 'Phone', 'Currency', 'Lead Time', ''].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s, i) => (
                  <tr key={s.id} style={{ background: 'transparent' }}>
                    <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
                    <td style={td}>{s.contactName || '—'}</td>
                    <td style={td}>{s.email || '—'}</td>
                    <td style={td}>{s.phone || '—'}</td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{s.currency || 'ZAR'}</td>
                    <td style={td}>{s.leadTimeDays} days</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => navigate(`/creditors?search=${encodeURIComponent(s.name)}`)}
                        style={{ background: 'none', border: 'none', color: 'var(--page-muted)', cursor: 'pointer', fontSize: 11, marginRight: 8 }}>
                        💳 Balance
                      </button>
                      <button onClick={() => openEdit(s)}
                        style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: 12, marginRight: 8 }}>
                        Edit
                      </button>
                      <button onClick={() => del(s.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12 }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const TABS = [
  { key: 'overview',   label: 'Overview' },
  { key: 'transfers',  label: 'Inter-Branch Transfers' },
  { key: 'order',      label: 'Order Builder' },
  { key: 'catalogue',  label: 'Product Catalogue' },
  { key: 'suppliers',  label: 'Suppliers' },
];

export default function StockOptimisation() {
  const { period } = useOutletContext() || {};
  const { selectedCompany } = useCompany();
  const [resolvedCompanyId, setResolvedCompanyId] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  // Sync tab when URL param changes (e.g. arriving from Creditors link)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) setActiveTab(tabParam);
  }, [searchParams]);

  // companyId from context (populated after hydration)
  const ctxCompanyId = selectedCompany?.id || selectedCompany?.companyId;

  useEffect(() => {
    if (ctxCompanyId) { setResolvedCompanyId(ctxCompanyId); return; }
    // Context not hydrated yet (PWA fresh launch — localStorage still has it)
    // Fall back to localStorage (same source pbiQuery uses for Dashboard)
    try {
      const uid = auth.currentUser?.uid;
      const key = uid ? `insightpro_company_${uid}` : null;
      if (key) {
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored);
          setResolvedCompanyId(parsed?.id || parsed?.companyId || null);
        }
      }
    } catch {}
  }, [ctxCompanyId]);

  const companyId = resolvedCompanyId;

  return (
    <div style={{ animation: 'fadeUp 0.4s ease both', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--page-text)', marginBottom: 6 }}>
          Stock Optimisation
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--page-muted)' }}>
          Overstock & understock analysis, inter-branch transfers, order builder with voice & photo, and supplier management.
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid var(--card-border)',
        marginBottom: 24, overflowX: 'auto',
      }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={tabBtn(activeTab === t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview'   && <TabOverview      companyId={companyId} period={period} />}
      {activeTab === 'transfers'  && <TabTransfers     companyId={companyId} period={period} />}
      {activeTab === 'order'      && <TabOrderBuilder  companyId={companyId} />}
      {activeTab === 'catalogue'  && <TabProductCatalogue companyId={companyId} />}
      {activeTab === 'suppliers'  && <TabSuppliers     companyId={companyId} />}
    </div>
  );
}
